import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Check } from 'lucide-react';
import { ttStatsAPI } from '../services/api';
import { Player } from '../types/api.types';
import { useAuth } from '../contexts/AuthContext';
import { getClassificationColor } from '../utils/classificationUtils';
import { notifications } from '@mantine/notifications';

interface SetProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang: 'en' | 'fr' | 'de' | 'it';
}

const SetProfileModal: React.FC<SetProfileModalProps> = ({ isOpen, onClose, lang }) => {
  const { setMyProfile } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isSettingProfile, setIsSettingProfile] = useState(false);

  const translations = {
    en: {
      title: 'Set Your Profile',
      searchPlaceholder: 'Search for your player profile...',
      noResults: 'No players found',
      tryDifferent: 'Try a different search term',
      selectProfile: 'Select this as my profile',
      profileSet: 'Profile Set!',
      profileSetMessage: 'Your profile has been updated successfully',
      close: 'Close',
    },
    fr: {
      title: 'Définir ton profil',
      searchPlaceholder: 'Cherche ton profil de joueur...',
      noResults: 'Aucun joueur trouvé',
      tryDifferent: 'Essaie un autre terme',
      selectProfile: 'Sélectionner comme mon profil',
      profileSet: 'Profil défini !',
      profileSetMessage: 'Ton profil a été mis à jour avec succès',
      close: 'Fermer',
    },
    de: {
      title: 'Profil festlegen',
      searchPlaceholder: 'Suche dein Spielerprofil...',
      noResults: 'Keine Spieler gefunden',
      tryDifferent: 'Versuche einen anderen Suchbegriff',
      selectProfile: 'Als mein Profil auswählen',
      profileSet: 'Profil festgelegt!',
      profileSetMessage: 'Dein Profil wurde erfolgreich aktualisiert',
      close: 'Schließen',
    },
    it: {
      title: 'Imposta il profilo',
      searchPlaceholder: 'Cerca il tuo profilo giocatore...',
      noResults: 'Nessun giocatore trovato',
      tryDifferent: 'Prova un altro termine',
      selectProfile: 'Seleziona come mio profilo',
      profileSet: 'Profilo impostato!',
      profileSetMessage: 'Il tuo profilo è stato aggiornato con successo',
      close: 'Chiudi',
    },
  };

  const t = translations[lang];

  const performSearch = useCallback(async () => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const data = await ttStatsAPI.searchPlayers(searchQuery);
      setSearchResults(data.players || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, performSearch]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedPlayer(null);
    }
  }, [isOpen]);

  const handleSelectPlayer = async (player: Player) => {
    if (!player.url) return;

    setIsSettingProfile(true);
    try {
      // Fetch full player data to get accurate classification
      const eloData = await ttStatsAPI.getPlayerElo(player.url);

      // Set as my profile with accurate data from API
      await setMyProfile(
        player.url,
        {
          name: eloData.player_name || player.name,
          club: eloData.club || player.club,
          elo: eloData.current_elo || eloData.official_elo || player.elo,
          classification: eloData.classification, // Use classification from API
          license_number: eloData.licenseNumber || player.url,
        }
      );

      setSelectedPlayer(player);

      // Show success animation then close
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error setting profile:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to set profile. Please try again.',
        color: 'red',
        autoClose: 3000,
      });
    } finally {
      setIsSettingProfile(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[102] p-4 transition-colors duration-300"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col transition-colors duration-300"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">{t.title}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors duration-300"
            >
              <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full px-6 py-3.5 pr-12 text-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-300"
                autoFocus
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {isSearching ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Search className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </motion.div>
                ) : (
                  <Search className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {searchQuery.length < 2 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400 px-6 transition-colors duration-300">
                <Search className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p>{t.searchPlaceholder}</p>
              </div>
            ) : isSearching ? (
              <div>
                {[1, 2, 3].map((item) => (
                  <motion.div
                    key={item}
                    className="px-6 py-4 border-b border-gray-50 dark:border-gray-700 last:border-b-0 transition-colors duration-300"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: item * 0.1 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0 mr-3">
                        <div
                          className="h-5 bg-gray-200 dark:bg-gray-700 rounded-md mb-2 relative overflow-hidden"
                          style={{ width: `${150 + item * 30}px` }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 dark:via-gray-600/40 to-transparent"
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                          />
                        </div>
                        <div
                          className="h-3 bg-gray-100 dark:bg-gray-600 rounded-md relative overflow-hidden"
                          style={{ width: `${100 + item * 20}px` }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 dark:via-gray-500/40 to-transparent"
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.2 }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-12 bg-gray-200 dark:bg-gray-700 rounded-lg relative overflow-hidden">
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 dark:via-gray-600/40 to-transparent"
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.3 }}
                          />
                        </div>
                        <div className="h-7 w-16 bg-gray-100 dark:bg-gray-600 rounded-lg relative overflow-hidden">
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 dark:via-gray-500/40 to-transparent"
                            animate={{ x: ['-100%', '200%'] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "linear", delay: 0.4 }}
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-12 px-6">
                <div className="w-16 h-16 bg-gray-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Search className="w-8 h-8 text-white" />
                </div>
                <p className="text-gray-900 dark:text-gray-100 font-medium mb-1 transition-colors duration-300">{t.noResults}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{t.tryDifferent}</p>
              </div>
            ) : (
              <div>
                {searchResults.map((player, index) => (
                  <motion.div
                    key={index}
                    className="group"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 + index * 0.05, ease: [0.25, 0.1, 0.25, 1.0] }}
                  >
                    <motion.button
                      className="w-full px-6 py-4 cursor-pointer transition-all duration-300 border-b border-gray-50 dark:border-gray-700 text-left disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                      onClick={() => handleSelectPlayer(player)}
                      disabled={isSettingProfile || selectedPlayer?.url === player.url}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="font-medium text-gray-900 dark:text-gray-100 truncate transition-colors duration-300">
                            {(() => {
                              // Convert "LastName, FirstName" to "FirstName LastName"
                              if (player.name.includes(',')) {
                                const [lastName, firstName] = player.name.split(',').map(s => s.trim());
                                return `${firstName} ${lastName}`;
                              }
                              return player.name;
                            })()}
                          </p>
                          {player.club && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1 transition-colors duration-300">
                              {player.club}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {player.classification && (() => {
                            const colors = getClassificationColor(player.classification);
                            return (
                              <span
                                className="text-xs font-bold px-2.5 py-1 rounded-lg whitespace-nowrap"
                                style={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                }}
                              >
                                {player.classification}
                              </span>
                            );
                          })()}
                          {player.elo && (
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 px-3 py-1 rounded-lg whitespace-nowrap transition-colors duration-300">
                              {player.elo}
                            </span>
                          )}
                          {selectedPlayer?.url === player.url && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-8 h-8 bg-[#059669] rounded-xl flex items-center justify-center"
                            >
                              <Check className="w-5 h-5 text-white" />
                            </motion.div>
                          )}
                        </div>
                      </div>
                    </motion.button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default SetProfileModal;
